import { StaticVariables } from "../../../../Utils/StaticVariables";

export type ShowFilter = "both" | "content" | "products";
export type PlatformFilter = string; // e.g. "All", "instagram", "MISC_AGG"

const miscAggregate = ["MISC", "MISC-product", "MISC-content"];

const normalizePlatformSelection = (
  platformFilter: PlatformFilter,
  showFilter: ShowFilter,
  isShopLinks: boolean
) => {
  if (!platformFilter || platformFilter === "All") return null;
  if (platformFilter === "MISC_AGG" || platformFilter === "MISC") {
    if (!isShopLinks || showFilter === "content") return ["MISC-content"];
    if (showFilter === "products") return ["MISC", "MISC-product"];
    return miscAggregate;
  }
  if (platformFilter === "post" || platformFilter === "story") return ["instagram"];
  return [platformFilter];
};

export const deriveShowPlatforms = (
  showFilter: ShowFilter,
  isShopLinks: boolean
): string[] => {
  if (!isShopLinks) return StaticVariables().CONTENT_PLATFORMS;
  if (showFilter === "content") return StaticVariables().CONTENT_PLATFORMS;
  if (showFilter === "products") return StaticVariables().PRODUCT_PLATFORMS;
  return StaticVariables().PLATFORMS as string[];
};

export const deriveAllowedPlatforms = (
  platformFilter: PlatformFilter,
  showFilter: ShowFilter,
  isShopLinks: boolean
): string[] => {
  const showPlatforms = deriveShowPlatforms(showFilter, isShopLinks).filter(Boolean);
  const platformSelection = normalizePlatformSelection(
    platformFilter,
    showFilter,
    isShopLinks
  );
  if (!platformSelection) return showPlatforms;
  const showSet = new Set(showPlatforms.map((platform) => platform.toLowerCase()));
  const selected = platformSelection.filter(Boolean);
  const inShow = selected.filter((platform) =>
    showSet.has(platform.toLowerCase())
  );
  if (inShow.length === selected.length) return selected;
  const extras = selected.filter((platform) =>
    platform.toLowerCase().startsWith("misc")
  );
  if (inShow.length) return Array.from(new Set([...inShow, ...extras]));
  // Stale / cross-mode platformFilter (e.g. ShopMy on a Content Keyword after
  // mis-edit as Product Keyword) must not win — fall back to the mode's full
  // platform set. Pre-add-component intersected and dropped these; returning
  // `selected` made content carousels look like product-only feeds.
  return showPlatforms;
};

export const filterPosts = (
  posts: any[] | undefined,
  platformFilter: PlatformFilter,
  showFilter: ShowFilter,
  isShopLinks: boolean
) => {
  const allowed = deriveAllowedPlatforms(platformFilter, showFilter, isShopLinks);
  const allowedSet = new Set(allowed.map((p) => (p || "").toLowerCase()));
  return (posts || []).filter((post) =>
    allowedSet.has(String(post?.platform || "").toLowerCase())
  );
};

export const readPlatformFilterFromDetails = (details: any): PlatformFilter => {
  if (!details) return "All";
  if (typeof details === "string") {
    try {
      const parsed = JSON.parse(details);
      return parsed?.platformFilter || "All";
    } catch {
      return "All";
    }
  }
  if (typeof details === "object" && details.platformFilter) {
    return details.platformFilter;
  }
  return "All";
};

export const writePlatformFilterToDetails = (
  existingDetails: any,
  platformFilter: PlatformFilter
) => {
  let parsed: Record<string, any> = {};
  if (existingDetails && typeof existingDetails === "string") {
    try {
      parsed = JSON.parse(existingDetails);
    } catch {
      parsed = {};
    }
  } else if (existingDetails && typeof existingDetails === "object") {
    parsed = { ...existingDetails };
  }
  return JSON.stringify({ ...parsed, platformFilter });
};

export const KEYWORD_CONTENT_PLATFORM_PILLS = [
  { id: "All", label: "All" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "youtube", label: "YouTube" },
  { id: "twitter", label: "X" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "podcast", label: "Podcast" },
];

export const KEYWORD_PRODUCT_PLATFORM_PILLS = [
  { id: "All", label: "All" },
  { id: "ShopMy", label: "ShopMy" },
  { id: "Amazon", label: "Amazon" },
  { id: "LTK", label: "LTK" },
  { id: "MISC", label: "Added By Me" },
];

export function defaultKeywordShowFilter(isShopLinks: boolean): ShowFilter {
  return isShopLinks ? "products" : "content";
}

/** Read Show scope from DB — used so legacy content/both rows aren't forced to products. */
export function resolveShowFilterFromComponent(
  shoppableContentFilter?: string | null,
  fallback: ShowFilter = "both"
): ShowFilter {
  const f = String(shoppableContentFilter || "")
    .toLowerCase()
    .trim();
  if (f === "content" || f === "products" || f === "both") return f;
  return fallback;
}

/** Product Keyword DB type — never infer from shoppable_content_filter alone. */
export function isProductKeywordType(type?: string | null): boolean {
  return String(type || "").toLowerCase().trim() === "query-links";
}

/**
 * Content Keyword DB types (legacy LiB `query-content`, website/LiB `content-series`).
 * Stale `shoppable_content_filter=products` on these rows must not reclassify them.
 */
export function isContentKeywordType(type?: string | null): boolean {
  const t = String(type || "").toLowerCase().trim();
  return t === "query-content" || t === "content-series";
}

/**
 * ShopMy / Amazon / LTK catalog RPC (`query_based_shop`) only for Product
 * Keyword rows whose Show scope is products. Legacy query-links with
 * content/both (and all Content Keywords) stay on `query_based_cont`.
 */
export function shouldUseShopKeywordRpc(
  type?: string | null,
  shoppableContentFilter?: string | null,
): boolean {
  if (!isProductKeywordType(type)) return false;
  return (
    resolveShowFilterFromComponent(shoppableContentFilter, "products") ===
    "products"
  );
}

const PRODUCT_CATALOG_PLATFORMS = new Set(["ltk", "shopmy", "amazon"]);

/** ShopMy / LTK / Amazon catalog rows — not shoppable social posts. */
export function isProductCatalogPost(post: unknown): boolean {
  if (!post || typeof post !== "object") return false;
  const row = post as { platform?: unknown; type?: unknown };
  const plat = String(row.platform || "").toLowerCase();
  const type = String(row.type || "").toUpperCase();
  return type === "PRODUCT" || PRODUCT_CATALOG_PLATFORMS.has(plat);
}

/** Content phrase feeds must never keep ShopMy/Amazon/LTK catalog tiles. */
export function filterOutProductCatalogPosts<T>(
  posts: T[] | undefined,
  isShopLinks: boolean,
): T[] {
  if (isShopLinks || !Array.isArray(posts)) return posts || [];
  return posts.filter((post) => !isProductCatalogPost(post));
}

export function getKeywordPlatformPills(
  isShopLinks: boolean,
  current?: string
) {
  const base = isShopLinks
    ? KEYWORD_PRODUCT_PLATFORM_PILLS
    : KEYWORD_CONTENT_PLATFORM_PILLS;
  const value = current || "All";
  if (base.some((option) => option.id === value)) return base;
  return [...base, { id: value, label: value }];
}

